import { Button, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';

import { Loading } from '../../../../Components/LoadingSpinner/Loading';
import { displayErrorDetails } from '../../../../Utils/displayError';
import AddPharmacy from '../../../Pharmacy/Components/AddPharmacy';
import SearchForPharmacy from '../../../Pharmacy/Components/FindPharmacy';
import { PharmacySubmit } from '../../../Pharmacy/Components/types';
import pharmacyStyles from '../../../Pharmacy/styles.module.scss';
import RemoveItemModal from '../../Components/RemoveItemModal/RemoveItemModal';

import styles from './collectorEditUser.module.scss';

interface CollectorPharmacyTabProps {
  user: UserProfile | null;
  onUserSaved?: () => void | Promise<void>;
  userRole?: string;
}

const CollectorPharmacyTab: React.FC<CollectorPharmacyTabProps> = ({ user, onUserSaved, userRole = UserRoleName.Collector }) => {
  const [render, setRender] = useState(true);
  const [loading, setLoading] = useState(true);
  const [pharmacyName, setPharmacyName] = useState('');
  const [pharmacyAddress, setPharmacyAddress] = useState('');
  const [pharmacyPhoneNumber, setPharmacyPhoneNumber] = useState('');
  const [findPharmacy, setFindPharmacy] = useState(false);
  const [addPharmacy, setAddPharmacy] = useState(false);
  const [objectId, setObjectId] = useState<string | null>(null);
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory | null>(null);
  const [removeItemModal, setRemoveItemModal] = useState(false);

  const getFormAnswers = useCallback(async (patientUserId: number): Promise<void> => {
    setRender(false);
    try {
      const { medical_history } = await listClinicalData(patientUserId, userRole);
      setMedicalHistory(medical_history ?? null);
      if (medical_history) {
        setObjectId(medical_history.id.toString());
      } else {
        setObjectId(null);
      }

      setPharmacyName(medical_history?.pharmacy_name || '');

      setPharmacyAddress(
        medical_history?.pharmacy_street_address && medical_history?.pharmacy_city
          ? [
              medical_history.pharmacy_street_address,
              [
                medical_history.pharmacy_city,
                [medical_history.pharmacy_state, medical_history.pharmacy_zip_code]
                  .filter(Boolean)
                  .join(' '),
              ]
                .filter(Boolean)
                .join(', '),
            ]
              .filter(Boolean)
              .join('\n')
          : '',
      );
      setPharmacyPhoneNumber(medical_history?.pharmacy_phone_number || '');
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setRender(true);
    }
  }, []);

  const AddNewPharmacy = useCallback(
    async (newPharmacy: PharmacySubmit): Promise<void> => {
      if (!user?.id) return;
      try {
        if (!objectId) {
          await addClinicalData(
            'medical_history',
            {
              user_id: user.id,
              under_care_of_physician: null,
              conditions_treated: null,
              taking_medication: null,
              medications: null,
              allergic_to_medications: null,
              medications_allergies: null,
              using_substances: [],
              interested_in_stopping: null,
              height: '',
              weight: '',
              pharmacy_name: newPharmacy.pharmacy_name,
              pharmacy_street_address: newPharmacy.pharmacy_street_address,
              pharmacy_city: newPharmacy.pharmacy_city,
              pharmacy_state: newPharmacy.pharmacy_state,
              pharmacy_zip_code: newPharmacy.pharmacy_zip_code,
              pharmacy_phone_number: newPharmacy.pharmacy_phone_number,
            },
            userRole,
          );
        } else {
          await updateClinicalData(
            'medical_history',
            objectId,
            {
              under_care_of_physician: medicalHistory?.under_care_of_physician ?? null,
              conditions_treated: medicalHistory?.conditions_treated ?? null,
              taking_medication: medicalHistory?.taking_medication ?? null,
              medications: medicalHistory?.medications ?? null,
              allergic_to_medications: medicalHistory?.allergic_to_medications ?? null,
              medications_allergies: medicalHistory?.medications_allergies ?? null,
              using_substances: medicalHistory?.using_substances ?? [],
              interested_in_stopping: medicalHistory?.interested_in_stopping ?? null,
              height: medicalHistory?.height ?? '',
              weight: medicalHistory?.weight ?? '',
              height_cm: medicalHistory?.height_cm ?? null,
              weight_kg: medicalHistory?.weight_kg ?? null,
              height_unit: medicalHistory?.height_unit,
              weight_unit: medicalHistory?.weight_unit,
              ...newPharmacy,
            },
            userRole,
          );
        }
        await getFormAnswers(user.id);
        setAddPharmacy(false);
        setFindPharmacy(false);
        toast.success(`You've successfully added ${newPharmacy.pharmacy_name} to your saved pharmacies`, {
          className: 'fs-unmask',
        });
        await onUserSaved?.();
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [user?.id, objectId, medicalHistory, userRole, getFormAnswers, onUserSaved],
  );

  const removePharmacy = useCallback(async (): Promise<void> => {
    if (!objectId || !user?.id) return;
    try {
      await updateClinicalData(
        'medical_history',
        objectId,
        {
          under_care_of_physician: medicalHistory?.under_care_of_physician ?? null,
          conditions_treated: medicalHistory?.conditions_treated ?? null,
          taking_medication: medicalHistory?.taking_medication ?? null,
          medications: medicalHistory?.medications ?? null,
          allergic_to_medications: medicalHistory?.allergic_to_medications ?? null,
          medications_allergies: medicalHistory?.medications_allergies ?? null,
          using_substances: medicalHistory?.using_substances ?? [],
          interested_in_stopping: medicalHistory?.interested_in_stopping ?? null,
          height: medicalHistory?.height ?? '',
          weight: medicalHistory?.weight ?? '',
          height_cm: medicalHistory?.height_cm ?? null,
          weight_kg: medicalHistory?.weight_kg ?? null,
          height_unit: medicalHistory?.height_unit,
          weight_unit: medicalHistory?.weight_unit,
          pharmacy_name: '',
          pharmacy_street_address: '',
          pharmacy_city: '',
          pharmacy_state: '',
          pharmacy_zip_code: '',
          pharmacy_phone_number: '',
        },
        userRole,
      );
      setRemoveItemModal(false);
      await getFormAnswers(user.id);
      toast.success('Pharmacy removed.', { className: 'fs-unmask' });
      await onUserSaved?.();
    } catch (error) {
      displayErrorDetails(error);
    }
  }, [objectId, medicalHistory, userRole, user?.id, getFormAnswers, onUserSaved]);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      setPharmacyName('');
      setPharmacyAddress('');
      setPharmacyPhoneNumber('');
      setObjectId(null);
      setFindPharmacy(false);
      setAddPharmacy(false);
      return;
    }
    (async (): Promise<void> => {
      setLoading(true);
      setFindPharmacy(false);
      setAddPharmacy(false);
      try {
        await getFormAnswers(user.id);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id, getFormAnswers]);

  if (!user) {
    return (
      <div className={styles.form}>
        <Typography className={styles.emptyState} component="p">
          No user loaded.
        </Typography>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.form}>
        <div className={styles.pageSpinnerWrap}>
          <Loading dotted isHeightAuto />
        </div>
      </div>
    );
  }

  if (findPharmacy) {
    return (
      <div className={styles.form}>
        <SearchForPharmacy
          setFindPharmacy={setFindPharmacy}
          setAddPharmacy={setAddPharmacy}
          AddNewPharmacy={AddNewPharmacy}
        />
      </div>
    );
  }

  if (addPharmacy) {
    return (
      <div className={styles.form}>
        <AddPharmacy setFindPharmacy={setFindPharmacy} setAddPharmacy={setAddPharmacy} AddNewPharmacy={AddNewPharmacy} />
      </div>
    );
  }

  if (render) {
    return (
      <div className={styles.form}>
        <div className={`${pharmacyStyles.pharmacyContainer} ${pharmacyStyles.pharmacyContainerNoPadding}`}>
          <h2 className="fs-unmask">My pharmacy</h2>
          {pharmacyName ? (
            <div className={`fs-exclude ${pharmacyStyles.pharmacyInfo}`} data-testid="saved-pharmacy-div">
              <div className={`fs-exclude ${pharmacyStyles.pharmacyInfoLeft}`}>
                <span className={pharmacyStyles.sectionContentRowDetailsName} data-testid="pharmacy-name">
                  {pharmacyName}
                </span>
                <span
                  className={pharmacyStyles.sectionContentRowDetailsAddress}
                  style={{ whiteSpace: 'pre-line' }}
                  data-testid="pharmacy-address"
                >
                  {pharmacyAddress}
                </span>
                {pharmacyPhoneNumber && (
                  <span className={pharmacyStyles.sectionContentRowDetailsAddress} data-testid="pharmacy-phone">
                    📞 {pharmacyPhoneNumber}
                  </span>
                )}
              </div>
              <div className={`fs-exclude ${pharmacyStyles.pharmacyInfoRight}`}>
                <Button
                  variant="text"
                  color="error"
                  onClick={() => setRemoveItemModal(true)}
                  data-testid="saved-pharmacy-remove"
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className={`fs-unmask ${pharmacyStyles.noPharmacy}`}>
              <span>{"You haven't added any pharmacies yet"}</span>
              <Button variant="contained" color="primary" data-testid="pharmacy-add-pharmacy" onClick={() => setFindPharmacy(true)}>
                Add A Pharmacy
              </Button>
            </div>
          )}
          {removeItemModal && (
            <RemoveItemModal
              isOpen={removeItemModal}
              toggleModal={setRemoveItemModal}
              itemName={`${pharmacyName}`}
              removeItem={removePharmacy}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.form}>
      <div className={styles.pageSpinnerWrap}>
        <Loading dotted isHeightAuto />
      </div>
    </div>
  );
};

export default CollectorPharmacyTab;
